// ─────────────────────────────────────────────────────────────────────────────
// Tutorial ("How to use") videos — the TOPIC REGISTRY.
//
// The videos themselves are NO LONGER edited here. They live in the `help_videos`
// table and are managed from the platform-admin panel:
//
//     Admin panel → Educational Videos
//
// Uploading a file there publishes it immediately — no commit, no Vercel
// Redeploy. (See supabase/migrations/20260807140000_help_videos.sql.)
//
// What this file still owns is the CONTRACT: which topics exist. Each entry
// must correspond to a real <HelpVideoButton topic="..."> somewhere in the app,
// because a topic with no button is a video nobody can ever open. Adding one is
// a two-step change:
//
//   1. Add it to HELP_TOPICS below and give it a default title + location here.
//   2. Add the matching value to the CHECK constraint in the migration.
//      Without that, saving the new topic fails with a constraint violation.
//
// The titles below are only a FALLBACK, used before the table has loaded and if
// it cannot be reached at all. Once a row exists, its title wins.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Every tutorial topic, in the order the admin panel lists them. Ordered to
 * follow the shape of a job — win the work, price it, schedule it, run it —
 * rather than alphabetically, so the panel reads like the product does.
 */
import type { Clave } from '../../i18n/textos';

export const HELP_TOPICS = [
    'dashboard',
    'clients',
    'estimates',
    'pricing-library',
    'follow-ups',
    'projects',
    'schedule',
    'service-routes',
    'team',
    'time-clock',
    'inventory',
    'finance',
] as const;

export type HelpTopic = (typeof HELP_TOPICS)[number];

export interface HelpVideo {
    /** Heading shown in the video box title bar. */
    title: string;
    /** Direct URL to the video file (mp4/webm). '' until one is uploaded. */
    src: string;
    /** Optional thumbnail shown before the user hits play. */
    poster?: string;
}

/**
 * Which screen each topic's "How to use" button lives on, as a translation key.
 *
 * These are the SIDEBAR's own keys on purpose. The admin panel is operated in
 * Spanish, and a card headed "Pricing Library" above a sidebar that says
 * "Precios" makes the operator work out the mapping themselves. Reusing
 * `nav.*` means the tab names every screen exactly as they see it named
 * everywhere else — and it stays correct for free if a screen is ever renamed.
 */
export const HELP_TOPIC_NAV_KEYS: Record<HelpTopic, Clave> = {
    dashboard: 'nav.panel',
    clients: 'nav.clientes',
    estimates: 'nav.estimados',
    'pricing-library': 'nav.precios',
    'follow-ups': 'nav.seguimientos',
    projects: 'nav.proyectos',
    schedule: 'nav.agenda',
    'service-routes': 'nav.rutas',
    team: 'nav.gente',
    'time-clock': 'nav.reloj',
    inventory: 'nav.inventario',
    finance: 'nav.finanzas',
};

/** Fallback titles. Superseded by the `title` column once the table loads. */
export const HELP_VIDEO_DEFAULTS: Record<HelpTopic, HelpVideo> = {
    dashboard: { title: 'How to use the Dashboard', src: '' },
    clients: { title: 'How to use Clients', src: '' },
    estimates: { title: 'How to use Estimates', src: '' },
    'pricing-library': { title: 'How to use the Pricing Library', src: '' },
    'follow-ups': { title: 'How to use Follow-ups', src: '' },
    projects: { title: 'How to use Projects', src: '' },
    schedule: { title: 'How to use the Schedule', src: '' },
    'service-routes': { title: 'How to use Service Routes', src: '' },
    team: { title: 'How to use the Team tab', src: '' },
    'time-clock': { title: 'How to use the Time Clock', src: '' },
    inventory: { title: 'How to use Inventory', src: '' },
    finance: { title: 'How to use Finance', src: '' },
};

export const isHelpTopic = (v: string): v is HelpTopic =>
    (HELP_TOPICS as readonly string[]).includes(v);

/**
 * Fallback lookup. Prefer `useHelpVideo().getVideo(topic)`, which returns the
 * uploaded video; this only ever returns the built-in default.
 */
export const getHelpVideo = (topic: HelpTopic): HelpVideo => HELP_VIDEO_DEFAULTS[topic];
